/**
 * Relationship Discovery Panel - Phase 2 of Knowledge Graph Design
 *
 * Allows users to:
 * - Discover FK-based and AI-inferred relationships
 * - Review and approve/reject discovered relationships
 * - Bulk approve high-confidence relationships
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GitBranch,
  ArrowRight,
  Check,
  X,
  Loader2,
  AlertCircle,
  RefreshCw,
  Sparkles,
  Database,
  Filter,
  CheckCircle2,
  XCircle,
  Clock,
  Zap,
  Brain,
  Link2,
} from 'lucide-react';
import {
  discoverRelationships,
  getDiscoveredRelationships,
  getPendingRelationships,
  approveRelationship,
  rejectRelationship,
  bulkApproveRelationships,
} from '../../lib/api/knowledgeGraphDesignApi';
import type {
  DiscoveredRelationship,
  RelationshipDiscoveryMethod,
  RelationshipStatus,
} from '../../types/knowledgeGraphDesign';
import Button from '../ui/Button';
import { Badge } from '../ui/Badge';
import Card from '../ui/Card';
import Spinner from '../ui/Spinner';

interface RelationshipDiscoveryPanelProps {
  caseId: string;
  systemIds: string[];
  systemNames: string[];
  currentUser: string;
  onRelationshipsApproved?: (relationshipIds: string[]) => void;
}

type FilterMode = 'all' | 'pending' | 'approved' | 'rejected' | 'fk_based' | 'ai_inferred';

export const RelationshipDiscoveryPanel: React.FC<RelationshipDiscoveryPanelProps> = ({
  caseId,
  systemIds,
  systemNames,
  currentUser,
  onRelationshipsApproved,
}) => {
  const queryClient = useQueryClient();

  // State for currently selected system in the tab view
  const [activeSystemIndex, setActiveSystemIndex] = useState(0);
  const activeSystemId = systemIds[activeSystemIndex];
  const activeSystemName = systemNames[activeSystemIndex];

  // UI State
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.6);
  const [includeAI, setIncludeAI] = useState(true);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingId, setRejectingId] = useState<string | null>(null);

  // Fetch discovered relationships for active system
  const {
    data: relationships,
    isLoading: loadingRelationships,
    error: relationshipsError,
    refetch: refetchRelationships,
  } = useQuery({
    queryKey: ['relationship-discovery', 'relationships', caseId, activeSystemId],
    queryFn: () => getDiscoveredRelationships(caseId, activeSystemId),
    enabled: !!activeSystemId,
  });

  // Discover relationships mutation
  const discoverMutation = useMutation({
    mutationFn: () => discoverRelationships(caseId, activeSystemId, includeAI, confidenceThreshold),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['relationship-discovery'] });
    },
  });

  // Approve relationship mutation
  const approveMutation = useMutation({
    mutationFn: (relationshipId: string) =>
      approveRelationship(caseId, activeSystemId, relationshipId, currentUser),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['relationship-discovery'] });
    },
  });

  // Reject relationship mutation
  const rejectMutation = useMutation({
    mutationFn: ({ relationshipId, reason }: { relationshipId: string; reason: string }) =>
      rejectRelationship(caseId, activeSystemId, relationshipId, currentUser, reason),
    onSuccess: () => {
      setRejectingId(null);
      setRejectReason('');
      queryClient.invalidateQueries({ queryKey: ['relationship-discovery'] });
    },
  });

  // Bulk approve mutation
  const bulkApproveMutation = useMutation({
    mutationFn: (relationshipIds: string[]) =>
      bulkApproveRelationships(caseId, activeSystemId, relationshipIds, currentUser),
    onSuccess: (data) => {
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['relationship-discovery'] });
      onRelationshipsApproved?.(Array.from(selectedIds));
    },
  });

  // Filter relationships
  const filteredRelationships = useMemo(() => {
    if (!relationships) return [];

    return relationships.filter((rel) => {
      switch (filterMode) {
        case 'pending':
          return rel.status === 'pending';
        case 'approved':
          return rel.status === 'approved';
        case 'rejected':
          return rel.status === 'rejected';
        case 'fk_based':
          return rel.discovery_method === 'fk_based';
        case 'ai_inferred':
          return rel.discovery_method === 'ai_inferred';
        default:
          return true;
      }
    });
  }, [relationships, filterMode]);

  // Statistics
  const stats = useMemo(() => {
    if (!relationships) return { total: 0, pending: 0, approved: 0, rejected: 0, fkBased: 0, aiInferred: 0 };

    return {
      total: relationships.length,
      pending: relationships.filter((r) => r.status === 'pending').length,
      approved: relationships.filter((r) => r.status === 'approved').length,
      rejected: relationships.filter((r) => r.status === 'rejected').length,
      fkBased: relationships.filter((r) => r.discovery_method === 'fk_based').length,
      aiInferred: relationships.filter((r) => r.discovery_method === 'ai_inferred').length,
    };
  }, [relationships]);

  // Toggle selection
  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Select all pending
  const selectAllPending = useCallback(() => {
    if (relationships) {
      const pendingIds = relationships
        .filter((r) => r.status === 'pending')
        .map((r) => r.relationship_id);
      setSelectedIds(new Set(pendingIds));
    }
  }, [relationships]);

  // Get method icon
  const getMethodIcon = (method: RelationshipDiscoveryMethod) => {
    switch (method) {
      case 'fk_based':
        return <Link2 className="w-4 h-4 text-blue-500" />;
      case 'ai_inferred':
        return <Brain className="w-4 h-4 text-purple-500" />;
      default:
        return <GitBranch className="w-4 h-4 text-neutral-500" />;
    }
  };

  // Get status badge
  const getStatusBadge = (status: RelationshipStatus) => {
    switch (status) {
      case 'approved':
        return (
          <Badge variant="default" className="bg-green-100 text-green-700">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="default" className="bg-red-100 text-red-700">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  // Get confidence color
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-600';
    if (confidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Render relationship card
  const renderRelationship = (rel: DiscoveredRelationship) => {
    const isSelected = selectedIds.has(rel.relationship_id);
    const isPending = rel.status === 'pending';
    const isRejecting = rejectingId === rel.relationship_id;

    return (
      <Card
        key={rel.relationship_id}
        className={`p-4 transition-all ${isSelected ? 'ring-2 ring-primary-500' : ''} ${
          rel.status === 'rejected' ? 'opacity-60' : ''
        }`}
      >
        <div className="flex items-start gap-4">
          {/* Selection checkbox */}
          {isPending && (
            <button
              onClick={() => toggleSelection(rel.relationship_id)}
              className={`w-5 h-5 rounded border flex items-center justify-center mt-1 ${
                isSelected
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : 'border-neutral-300 hover:border-primary-400'
              }`}
            >
              {isSelected && <Check className="w-3 h-3" />}
            </button>
          )}

          {/* Relationship info */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {getMethodIcon(rel.discovery_method)}
              <span className="font-medium text-sm">
                {rel.discovery_method === 'fk_based' ? 'Foreign Key' : 'AI Inferred'}
              </span>
              {getStatusBadge(rel.status)}
              <span className={`text-sm font-medium ${getConfidenceColor(rel.confidence)}`}>
                {Math.round(rel.confidence * 100)}% confidence
              </span>
            </div>

            {/* Source -> Target visualization */}
            <div className="flex items-center gap-3 bg-neutral-50 rounded-lg p-3">
              <div className="flex-1 text-center">
                <div className="font-mono text-sm font-medium">{rel.source_table_name}</div>
                <div className="text-xs text-neutral-500">{rel.source_column}</div>
              </div>
              <div className="flex flex-col items-center">
                <ArrowRight className="w-5 h-5 text-primary-500" />
                <span className="text-xs font-medium text-primary-600 mt-0.5">
                  {rel.relationship_type}
                </span>
                <span className="text-xs text-neutral-400">{rel.cardinality.replace('_', ':')}</span>
              </div>
              <div className="flex-1 text-center">
                <div className="font-mono text-sm font-medium">{rel.target_table_name}</div>
                <div className="text-xs text-neutral-500">{rel.target_column}</div>
              </div>
            </div>

            {/* Reason */}
            <p className="text-sm text-neutral-600 mt-2">{rel.reason}</p>

            {/* Rejection reason if rejected */}
            {rel.status === 'rejected' && rel.rejected_reason && (
              <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                <span className="font-medium">Rejection reason:</span> {rel.rejected_reason}
              </div>
            )}

            {/* Rejection input */}
            {isRejecting && (
              <div className="mt-3 p-3 bg-neutral-100 rounded-lg">
                <label className="block text-sm font-medium mb-2">Reason for rejection:</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full p-2 border border-neutral-200 rounded text-sm"
                  rows={2}
                  placeholder="Enter reason for rejecting this relationship..."
                />
                <div className="flex justify-end gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setRejectingId(null);
                      setRejectReason('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() =>
                      rejectMutation.mutate({
                        relationshipId: rel.relationship_id,
                        reason: rejectReason,
                      })
                    }
                    loading={rejectMutation.isPending}
                    disabled={!rejectReason.trim()}
                  >
                    Confirm Reject
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          {isPending && !isRejecting && (
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => approveMutation.mutate(rel.relationship_id)}
                loading={approveMutation.isPending}
                className="text-green-600 hover:bg-green-50"
              >
                <Check className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRejectingId(rel.relationship_id)}
                className="text-red-600 hover:bg-red-50"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </Card>
    );
  };

  // Loading state
  if (loadingRelationships && !relationships) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="w-8 h-8" />
        <span className="ml-3 text-neutral-600">Loading relationships...</span>
      </div>
    );
  }

  // Error state
  if (relationshipsError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-red-600">
        <AlertCircle className="w-8 h-8 mb-2" />
        <span>Failed to load relationships</span>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => refetchRelationships()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-neutral-200">
        {/* System tabs (only show if multiple systems) */}
        {systemIds.length > 1 && (
          <div className="flex items-center gap-1 px-4 pt-3 pb-0 border-b border-neutral-100 bg-neutral-50">
            {systemNames.map((name, index) => (
              <button
                key={systemIds[index]}
                onClick={() => setActiveSystemIndex(index)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeSystemIndex === index
                    ? 'bg-white text-primary-700 border border-b-0 border-neutral-200'
                    : 'text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <GitBranch className="w-5 h-5 text-primary-600" />
              <div>
                <h3 className="font-semibold text-lg">Relationship Discovery</h3>
                <p className="text-sm text-neutral-500">{activeSystemName}</p>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => discoverMutation.mutate()}
              loading={discoverMutation.isPending}
            >
              <Zap className="w-4 h-4 mr-1" />
              Discover Relationships
            </Button>
          </div>

          {/* Discovery options */}
          <div className="flex items-center gap-4 p-3 bg-neutral-50 rounded-lg">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeAI}
                onChange={(e) => setIncludeAI(e.target.checked)}
                className="rounded border-neutral-300"
              />
              Include AI inference
            </label>
            <div className="flex items-center gap-2">
              <label className="text-sm text-neutral-600">Min confidence:</label>
              <input
                type="range"
                min="0.3"
                max="0.9"
                step="0.1"
                value={confidenceThreshold}
                onChange={(e) => setConfidenceThreshold(parseFloat(e.target.value))}
                className="w-24"
              />
              <span className="text-sm font-medium">{Math.round(confidenceThreshold * 100)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex items-center gap-4 p-4 border-b border-neutral-100 bg-neutral-50">
        <button
          onClick={() => setFilterMode('all')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            filterMode === 'all' ? 'bg-primary-100 text-primary-700' : 'hover:bg-neutral-200'
          }`}
        >
          All ({stats.total})
        </button>
        <button
          onClick={() => setFilterMode('pending')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            filterMode === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'hover:bg-neutral-200'
          }`}
        >
          <Clock className="w-4 h-4 inline mr-1" />
          Pending ({stats.pending})
        </button>
        <button
          onClick={() => setFilterMode('approved')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            filterMode === 'approved' ? 'bg-green-100 text-green-700' : 'hover:bg-neutral-200'
          }`}
        >
          <CheckCircle2 className="w-4 h-4 inline mr-1" />
          Approved ({stats.approved})
        </button>
        <button
          onClick={() => setFilterMode('rejected')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            filterMode === 'rejected' ? 'bg-red-100 text-red-700' : 'hover:bg-neutral-200'
          }`}
        >
          <XCircle className="w-4 h-4 inline mr-1" />
          Rejected ({stats.rejected})
        </button>
        <div className="border-l border-neutral-300 h-6 mx-2" />
        <button
          onClick={() => setFilterMode('fk_based')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            filterMode === 'fk_based' ? 'bg-blue-100 text-blue-700' : 'hover:bg-neutral-200'
          }`}
        >
          <Link2 className="w-4 h-4 inline mr-1" />
          FK ({stats.fkBased})
        </button>
        <button
          onClick={() => setFilterMode('ai_inferred')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            filterMode === 'ai_inferred' ? 'bg-purple-100 text-purple-700' : 'hover:bg-neutral-200'
          }`}
        >
          <Brain className="w-4 h-4 inline mr-1" />
          AI ({stats.aiInferred})
        </button>
      </div>

      {/* Bulk actions */}
      {stats.pending > 0 && (
        <div className="flex items-center justify-between p-3 bg-yellow-50 border-b border-yellow-100">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-yellow-600" />
            <span className="text-sm text-yellow-700">
              {stats.pending} relationships pending review
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={selectAllPending}>
              Select All Pending
            </Button>
            {selectedIds.size > 0 && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => bulkApproveMutation.mutate(Array.from(selectedIds))}
                loading={bulkApproveMutation.isPending}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Approve Selected ({selectedIds.size})
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredRelationships.length === 0 ? (
          <div className="text-center py-12 text-neutral-500">
            <GitBranch className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
            <p>No relationships found</p>
            <p className="text-sm mt-1">
              Click "Discover Relationships" to find connections between tables
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredRelationships.map(renderRelationship)}
          </div>
        )}
      </div>
    </div>
  );
};

export default RelationshipDiscoveryPanel;
